import { gql } from "@apollo/client";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import { useEffect, useState } from "react";
import { FruitDatalistQuery, FruitDatalistQueryVariables, SortEnumType } from "types/graphql";
import { useContext } from "react";
import { getAppContext } from 'config/AppContext';
import { graph } from "config/axios-context";
import { NotNull } from "types/common-types";
import AppLink from "components/common/AppLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppRoutes } from "config/router";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import Translate from "components/common/Translate";
import If from "components/common/If";
import { deleteFruit } from "types/agriculture/fruit-types";
import { Roles } from "utilities/app-utilities";
import Button from "components/common/button/Button";
const DELETE_MODAL_HASH = 'fruit-datalist-delete-modal';
const fruitDatalistQuery = gql`
query FruitDatalist($where: FruitFilterInput, $order: [FruitSortInput!], $skip:Int, $take: Int) {
    fruits {
      items(where: $where, order: $order, skip:$skip, take:$take) {
        id,
        name
      }
    }
  }
`;

const FruitDatalist = () => {
    const [state, setState] = useState<ComponentStateTypes>('loading');
    const [data, setData] = useState<NotNull<FruitDatalistQuery['fruits']>['items']>();
    const context = getAppContext(useContext);

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setState('loading');

        try {
            const response = await graph<FruitDatalistQuery, FruitDatalistQueryVariables>({
                query: fruitDatalistQuery,
                variables: {
                    order: [
                        {
                            name: SortEnumType.Asc
                        }   
                    ],
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            if (!response.data) setState('nodata');

            setState('');
            setData(response.data!.fruits!.items);
        } catch (error) {
            setState('error');
            context.notify('error', 'error.loading-data')
            throw error;
        }
    }

    const onDelete = async () => {
        try {
            const response = await deleteFruit(Number(context.modal.getData(DELETE_MODAL_HASH)));
            if(response) {
                context.notify('success', 'request.delete.success')
                await loadData();
            } else {
                context.notify('error', 'request.delete.error');    
            }
        } catch (error) {
            context.notify('error', 'request.delete.error');
            throw error;
        }
    }

    return (
        <>
            <ComponentState state={state}>
                <ul>
                    <If condition={context.userHasRole(Roles.AGRICULTURE_SALES_WRITE)}>
                        <li className="border-b theme-border pb-2">
                            <AppLink to={AppRoutes.AGRICULTURE_FRUIT_FORM}>
                                <Translate i18nKey="button.add-new" />
                            </AppLink>
                        </li>
                    </If>
                    {data?.map(i => {
                        return (
                            <li key={i!.id} className="border-b border-transparent hover:border-primary">
                                <div className="flex">
                                    <div className="w-full my-auto">
                                        {i?.name}
                                    </div>
                                    <If condition={i!.id > 1 && context.userHasRole(Roles.AGRICULTURE_SALES_WRITE)}>
                                        <div>
                                            <div className="grid-cols-2 gap-4 grid text text-center">
                                                <div className="p-2 my-auto text-center">
                                                    <AppLink className="link" to={AppRoutes.AGRICULTURE_FRUIT_EDIT} params={{ id: i?.id }}>
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </AppLink>
                                                </div>
                                                <div className="p-2 my-auto text-center">
                                                    <Button title="button.delete" className="link text-danger" onClick={() => context.modal.open(DELETE_MODAL_HASH, i.id)}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </If>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </ComponentState>
            <ConfirmationModal text="Delete?" name={DELETE_MODAL_HASH} onConfirm={onDelete} />
        </>
    );
};

export default FruitDatalist;
